import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Gavel,
  Info,
  MapPin,
  LogIn,
  Search,
  Send,
} from "lucide-react";
import { formatAmount } from "@/utils/formatAmount";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useAuthStore } from "@/store/auth";
import { portalUrl } from "@/features/rfq/publicSite";
import { ProceedingMdcpScopeMap } from "@/features/rfq/components/ProceedingMdcpScopeMap";
import {
  usePublicProceeding,
  usePublicProceedings,
  useSubmitAccountRequest,
  type PublicListing,
} from "@/features/rfq/api/public";

/**
 * Publiczna strona postępowań ofertowych GK ELEKTIM — bez logowania.
 *
 * Cel jest jeden: żeby firma, której jeszcze nie znamy, trafiła tu sama i zobaczyła,
 * że mamy dla niej robotę. Dlatego pokazujemy warunki, a nie tylko nagłówek — bez
 * terminu i zakresu nikt nie oceni, czy to dla niego.
 *
 * Czego tu NIE MA i nie ma być: załączników (dokumentacja to nasza własność),
 * kontaktu do kupującego (adres na otwartej stronie to zaproszenie dla spamu) ani
 * czegokolwiek o złożonych ofertach. Złożenie oferty wymaga konta — przycisk
 * prowadzi do prośby o jego założenie, którą i tak rozpatruje GK.
 *
 * Zalogowany widzi DOKŁADNIE TO SAMO — witryna zostaje witryną, także dla niego.
 * Dostaje tylko pasek z przejściem do klasycznego portalu, gdzie czeka pełny widok
 * (z załącznikami, pytaniami i formularzem oferty). Przenosiny są jego decyzją:
 * ktoś, kto celowo wszedł na publiczną stronę, nie chce być z niej wyrzucany.
 */

/**
 * Dokąd prowadzi przycisk „przejdź do portalu": podwykonawca z prawem do postępowań
 * ma swój widok ofertowy, wewnętrzny z dostępem do RFQ — panel. Kto ma konto, ale nie
 * ma żadnego z tych praw (np. pracownik biura), dostaje pulpit.
 */
export function fullViewPath(
  user: ReturnType<typeof useAuthStore.getState>["user"],
  proceedingId?: string,
): string | null {
  if (!user) return null;
  if (user.role === "contractor") {
    return user.can_access_rfq
      ? proceedingId
        ? `/contractor/proceedings/${proceedingId}`
        : "/contractor/proceedings"
      : null;
  }
  if (user.has_rfq_view_permission || user.is_ceo) {
    return proceedingId
      ? `/rfq/proceedings/${proceedingId}`
      : "/rfq/proceedings";
  }
  return null;
}

/**
 * Pasek dla zalogowanego: witryna zostaje, ale jedno kliknięcie prowadzi do portalu.
 *
 * Na subdomenie link jest bezwzględny (inny origin) — `portalUrl` to załatwia; na
 * domenie głównej zostaje zwykłą ścieżką.
 */
function PortalBar({ proceedingId }: { proceedingId?: string }) {
  const { user } = useAuthStore();
  if (!user) return null;
  const target = fullViewPath(user, proceedingId) ?? "/";
  const label =
    fullViewPath(user, proceedingId) === null
      ? "Przejdź do portalu ELGW 5"
      : proceedingId
        ? "Otwórz to postępowanie w portalu ELGW 5"
        : "Przejdź do portalu ELGW 5";
  return (
    <div className="mb-5 flex flex-wrap items-center justify-between gap-3 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-4 py-3">
      <p className="text-sm text-[var(--color-text-secondary)]">
        Jesteś zalogowany jako{" "}
        <strong className="text-[var(--color-text-primary)]">
          {user.first_name || user.username}
        </strong>
        . Ta strona to publiczna wizytówka — pełne dane, załączniki i składanie
        ofert są w portalu.
      </p>
      <a href={portalUrl(target)} className="shrink-0">
        <Button variant="secondary">
          <LogIn size={14} className="mr-1.5 inline" /> {label}
        </Button>
      </a>
    </div>
  );
}

const RODO =
  "Administratorem danych podanych w formularzu jest GK ELEKTIM. Dane " +
  "przetwarzamy wyłącznie po to, żeby odpowiedzieć na zgłoszenie i rozważyć " +
  "nawiązanie współpracy. Podanie danych jest dobrowolne, ale bez nich nie mamy " +
  "jak się odezwać. Zgodę można wycofać w każdej chwili, pisząc na zakupy@elektim.pl.";

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-[var(--color-bg-base)]">
      <header className="border-b border-[var(--color-border)] bg-white dark:bg-gray-900">
        <div className="mx-auto flex max-w-4xl items-center gap-3 px-5 py-4">
          <Gavel size={20} className="text-[var(--color-primary)]" />
          <div>
            <p className="text-lg font-bold leading-tight text-[var(--color-text-primary)]">
              Postępowania ofertowe GK ELEKTIM
            </p>
            <p className="text-xs text-[var(--color-text-muted)]">
              Zapytania do podwykonawców
            </p>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-5 py-8">{children}</main>
      <footer className="mx-auto max-w-4xl px-5 pb-10 text-xs text-[var(--color-text-muted)]">
        Składanie ofert wymaga konta w systemie ELGW 5. Konta zakłada wyłącznie
        GK ELEKTIM po weryfikacji firmy.
      </footer>
    </div>
  );
}

export function PublicProceedingsPage() {
  usePageTitle("Postępowania ofertowe GK ELEKTIM");
  const { data, isLoading } = usePublicProceedings();
  const [search, setSearch] = useState("");

  const q = search.trim().toLowerCase();
  const list = useMemo(
    () =>
      (data ?? []).filter(
        (p) =>
          !q ||
          `${p.number} ${p.title} ${p.description}`.toLowerCase().includes(q),
      ),
    [data, q],
  );

  if (isLoading) return <PageLoader />;

  return (
    <Shell>
      <PortalBar />
      <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
        Aktualne zapytania ofertowe
      </h1>
      <div className="mt-1 h-0.5 w-8 bg-[var(--color-primary)]" />
      <p className="mt-3 max-w-2xl text-sm text-[var(--color-text-secondary)]">
        Publiczna lista trwających postępowań, w których GK ELEKTIM szuka
        wykonawców. Szczegółowa dokumentacja jest dostępna po zalogowaniu — jeśli
        chcą Państwo startować, prosimy o zgłoszenie się po konto.
      </p>
      <div className="mt-4 flex max-w-2xl items-start gap-2 border-l-4 border-[var(--color-primary)] bg-white p-3 text-sm text-[var(--color-text-secondary)] dark:bg-gray-800">
        <Info size={16} className="mt-0.5 shrink-0 text-[var(--color-primary)]" />
        <p>
          Prezentujemy tu wyłącznie postępowania w pełni jawne.{" "}
          <strong className="text-[var(--color-text-primary)]">
            Część zapytań kierujemy wyłącznie do wykonawców z nadanymi
            kwalifikacjami
          </strong>{" "}
          — te są widoczne dopiero po zalogowaniu, w zakresie przyznanych
          kategorii prac i obszaru działania. Warto mieć konto: zaproszenia do
          takich postępowań przychodzą automatycznie.
        </p>
      </div>

      <div className="relative mt-6">
        <Search
          size={14}
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"
        />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Szukaj po numerze, tytule lub opisie…"
          className="w-full border border-[var(--color-border)] bg-transparent py-2 pl-8 pr-3 text-sm text-[var(--color-text-primary)]"
        />
      </div>

      <div className="mt-5 space-y-2">
        {list.length === 0 ? (
          <p className="border border-dashed border-[var(--color-border)] px-4 py-10 text-center text-sm text-[var(--color-text-muted)]">
            {data?.length
              ? "Nic nie pasuje do wyszukiwania."
              : "Obecnie nie prowadzimy otwartych postępowań. Zapraszamy wkrótce."}
          </p>
        ) : (
          list.map((p) => <Card key={p.id} proceeding={p} />)
        )}
      </div>

      <div className="mt-10 border-t border-[var(--color-border)] pt-6">
        <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
          Chcą Państwo z nami pracować?
        </h2>
        <p className="mt-1 max-w-2xl text-sm text-[var(--color-text-secondary)]">
          Prosimy o zostawienie danych — odezwiemy się i, po weryfikacji firmy,
          założymy konto w systemie.
        </p>
        <AccountRequestForm />
      </div>
    </Shell>
  );
}

function Card({ proceeding }: { proceeding: PublicListing }) {
  const hoursLeft =
    (new Date(proceeding.submission_deadline).getTime() - Date.now()) /
    3_600_000;
  return (
    <Link
      to={`/postepowania/${proceeding.id}`}
      className="block border border-[var(--color-border)] border-l-4 border-l-[var(--color-primary)] bg-white p-4 hover:bg-[var(--color-bg-elevated)] dark:bg-gray-800"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-[var(--color-text-primary)]">
            {proceeding.title}
          </h3>
          <p className="font-mono text-xs text-[var(--color-text-muted)]">
            {proceeding.number}
          </p>
        </div>
        <Badge color={hoursLeft < 48 ? "red" : "green"}>
          {hoursLeft < 48 ? "Kończy się" : "Otwarte"}
        </Badge>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-[var(--color-text-secondary)]">
        <span className="flex items-center gap-1">
          <Clock size={12} /> Oferty do:{" "}
          {new Date(proceeding.submission_deadline).toLocaleString("pl", {
            dateStyle: "short",
            timeStyle: "short",
          })}
        </span>
        {proceeding.voivodeship_label && (
          <span className="flex items-center gap-1">
            <MapPin size={12} /> {proceeding.voivodeship_label}
            {proceeding.county_labels.length > 0 && ` (${proceeding.county_labels.join(", ")})`}
          </span>
        )}
      </div>
    </Link>
  );
}

export function PublicProceedingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const full = fullViewPath(user, id);
  const { data, isLoading, isError } = usePublicProceeding(id);
  const [asking, setAsking] = useState(false);
  usePageTitle(data ? data.title : "Postępowanie ofertowe");

  if (isLoading) return <PageLoader />;
  if (isError || !data)
    return (
      <Shell>
        <Link
          to="/postepowania"
          className="mb-5 inline-flex items-center gap-1 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
        >
          <ArrowLeft size={14} /> Wszystkie postępowania
        </Link>
        <p className="text-sm text-[var(--color-text-secondary)]">
          Nie znaleziono postępowania. Mogło zostać zakończone albo termin
          składania ofert już minął.
        </p>
      </Shell>
    );

  return (
    <Shell>
      <Link
        to="/postepowania"
        className="mb-5 inline-flex items-center gap-1 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
      >
        <ArrowLeft size={14} /> Wszystkie postępowania
      </Link>
      <PortalBar proceedingId={data.id} />

      <p className="font-mono text-xs text-[var(--color-text-muted)]">
        {data.number}
      </p>
      <h1 className="mt-1 text-2xl font-bold text-[var(--color-text-primary)]">
        {data.title}
      </h1>
      <div className="mt-1 h-0.5 w-8 bg-[var(--color-primary)]" />

      <section className="mt-6 grid gap-px bg-[var(--color-border)] sm:grid-cols-2 lg:grid-cols-4">
        <Tile icon={<Clock size={12} />} label="Termin składania ofert">
          {new Date(data.submission_deadline).toLocaleString("pl", {
            dateStyle: "short",
            timeStyle: "short",
          })}
        </Tile>
        <Tile icon={<Calendar size={12} />} label="Termin realizacji">
          {data.delivery_deadline
            ? new Date(data.delivery_deadline).toLocaleDateString("pl")
            : "—"}
        </Tile>
        <Tile icon={<MapPin size={12} />} label="Województwo">
          {data.voivodeship_label || "bez znaczenia"}
          {data.county_labels.length > 0 && (
            <span className="block text-xs text-[var(--color-text-muted)]">
              powiaty: {data.county_labels.join(", ")}
            </span>
          )}
        </Tile>
        <Tile label="Cena maksymalna">
          {data.max_price_net
            ? `${formatAmount(data.max_price_net)} zł netto`
            : "nie podano"}
          {data.price_basis && (
            <span className="mt-0.5 block text-xs font-normal text-[var(--color-text-secondary)]">
              Cena {data.price_basis}
            </span>
          )}
        </Tile>
      </section>

      <section className="mt-6">
        <h2 className="mb-2 border-l-2 border-[var(--color-primary)] pl-2 text-base font-semibold text-[var(--color-text-primary)]">
          Opis zamówienia
        </h2>
        <div className="bg-white p-4 dark:bg-gray-800">
          <p className="whitespace-pre-wrap text-sm text-[var(--color-text-secondary)]">
            {data.description || "Brak dodatkowego opisu."}
          </p>
        </div>
      </section>

      {data.positions_detail.length > 0 && (
        <section className="mt-6">
          <h2 className="mb-2 border-l-2 border-[var(--color-primary)] pl-2 text-base font-semibold text-[var(--color-text-primary)]">
            Zakres prac
          </h2>
          <div className="bg-white p-4 dark:bg-gray-800">
            <ul className="space-y-1 text-sm">
              {data.positions_detail.map((p) => (
                <li key={p.id} className="flex items-start gap-2">
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[var(--color-text-muted)]" />
                  <span className="text-[var(--color-text-primary)]">
                    {p.name}
                    <span className="ml-1 text-xs text-[var(--color-text-muted)]">
                      ({p.category_name})
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Obrys zakresu MDCP na podkładzie OSM — firma z ulicy ma zobaczyć, GDZIE i jak
          duża jest robota, zanim w ogóle poprosi o konto. Punktów zdjęciowych tu nie ma:
          serializer publiczny ich nie zwraca, a mapa dostaje pustą listę. */}
      {data.mdcp_outline && (
        <div className="mt-6">
          <ProceedingMdcpScopeMap scope={{ points: [], buffer: data.mdcp_outline }} />
        </div>
      )}

      {/* Sedno publicznej strony: przycisk jest, ale prowadzi do rejestracji.
          Oferty składa się wyłącznie z konta — inaczej nie wiadomo, kto licytuje. */}
      <section className="mt-8 border-2 border-[var(--color-primary)] bg-white p-5 dark:bg-gray-800">
        <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
          Chcą Państwo złożyć ofertę?
        </h2>
        <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
          Oferty przyjmujemy wyłącznie przez system ELGW 5, po zalogowaniu.
          Jeśli mają już Państwo konto — prosimy się zalogować. Jeśli nie,
          prosimy o zgłoszenie: odezwiemy się i po weryfikacji firmy założymy
          konto.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {/* Zalogowany z prawem do ofert nie prosi o konto — ma je. Idzie prosto
              do formularza oferty w portalu. */}
          {full ? (
            <a href={portalUrl(full)}>
              <Button>
                <Send size={14} className="mr-1.5 inline" />
                {/* Wewnętrzny nie licytuje — jemu portal otwiera panel, nie formularz. */}
                {user?.role === "contractor"
                  ? "Złóż ofertę w portalu ELGW 5"
                  : "Otwórz panel postępowania"}
              </Button>
            </a>
          ) : (
            <>
              <Button onClick={() => setAsking(true)}>
                <Send size={14} className="mr-1.5 inline" /> Złóż ofertę —
                poproś o konto
              </Button>
              <a href={portalUrl("/auth/login")}>
                <Button variant="secondary">Mam już konto — zaloguj się</Button>
              </a>
            </>
          )}
        </div>
        {asking && !full && <AccountRequestForm proceedingId={data.id} />}
      </section>
    </Shell>
  );
}

/** Formularz „poproś o konto" — publiczny, więc z pułapką na boty i klauzulą RODO. */
export function AccountRequestForm({
  proceedingId,
}: {
  proceedingId?: string;
}) {
  const submit = useSubmitAccountRequest();
  const [form, setForm] = useState({
    company_name: "",
    nip: "",
    contact_person: "",
    email: "",
    phone: "",
    message: "",
    website: "", // honeypot
  });
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  if (sent)
    return (
      <div className="mt-4 border border-green-600/40 bg-green-50 p-4 text-sm text-green-800 dark:bg-green-950/40 dark:text-green-300">
        Dziękujemy — zgłoszenie do nas dotarło. Odezwiemy się na podany adres.
      </div>
    );

  const set =
    (key: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const send = () => {
    if (!form.company_name.trim()) return setError("Podaj nazwę firmy.");
    if (!form.contact_person.trim()) return setError("Podaj osobę kontaktową.");
    if (!form.email.trim()) return setError("Podaj adres e-mail.");
    if (!consent)
      return setError("Bez zgody na kontakt nie możemy odpowiedzieć.");
    setError("");
    submit.mutate(
      {
        ...form,
        consent,
        ...(proceedingId ? { proceeding: proceedingId } : {}),
      },
      {
        onSuccess: () => setSent(true),
        onError: (err: unknown) => {
          const response = (err as { response?: { status?: number } }).response;
          setError(
            response?.status === 429
              ? "Zbyt wiele zgłoszeń z tego adresu. Prosimy spróbować później."
              : "Nie udało się wysłać zgłoszenia. Prosimy spróbować ponownie.",
          );
        },
      },
    );
  };

  return (
    <div className="mt-4 space-y-3 border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Input
          label="Nazwa firmy *"
          value={form.company_name}
          onChange={set("company_name")}
        />
        <Input label="NIP" value={form.nip} onChange={set("nip")} />
        <Input
          label="Osoba kontaktowa *"
          value={form.contact_person}
          onChange={set("contact_person")}
        />
        <Input
          label="E-mail *"
          value={form.email}
          onChange={set("email")}
          type="email"
        />
        <Input label="Telefon" value={form.phone} onChange={set("phone")} />
      </div>
      <label className="block">
        <span className="mb-1 block font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
          Czym się Państwo zajmują / czego dotyczy zgłoszenie
        </span>
        <textarea
          value={form.message}
          onChange={set("message")}
          rows={3}
          className="w-full border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)]"
        />
      </label>

      {/* Pułapka na boty: ukryta przed człowiekiem, kusząca dla automatu. */}
      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={form.website}
        onChange={set("website")}
        className="hidden"
      />

      <label className="flex items-start gap-2 text-xs text-[var(--color-text-secondary)]">
        <input
          type="checkbox"
          className="mt-0.5 shrink-0"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
        />
        <span>
          Wyrażam zgodę na kontakt w sprawie tego zgłoszenia. *
          <span className="mt-1 block text-[var(--color-text-muted)]">
            {RODO}
          </span>
        </span>
      </label>

      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
      {/* Bez zgody przycisk jest martwy — zgoda to warunek przetwarzania,
          więc formularz nie może nawet udawać, że da się wysłać bez niej. */}
      <Button onClick={send} disabled={submit.isPending || !consent}>
        <Send size={14} className="mr-1.5 inline" />
        {submit.isPending ? "Wysyłanie…" : "Wyślij zgłoszenie"}
      </Button>
      {!consent && (
        <p className="mt-2 text-xs text-[var(--color-text-muted)]">
          Zaznacz zgodę na kontakt, aby wysłać zgłoszenie.
        </p>
      )}
    </div>
  );
}

function Input({
  label,
  ...props
}: { label: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="block">
      <span className="mb-1 block font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
        {label}
      </span>
      <input
        {...props}
        className="w-full border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)]"
      />
    </label>
  );
}

function Tile({
  icon,
  label,
  children,
}: {
  icon?: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white p-3 dark:bg-gray-800">
      <p className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
        {icon}
        {label}
      </p>
      <p className="mt-0.5 text-sm text-[var(--color-text-primary)]">
        {children}
      </p>
    </div>
  );
}
